import {
  Checkbox,
  IconButton,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { ExpandLess, ExpandMore, InfoOutlined } from '@mui/icons-material'
import { com } from '@eidu/web'
import { PermissionWithId } from '../input/PermissionsInput'
import SectionShell from './SectionShell'
import CellEditor from './CellEditor'
import { ColDef, ENTITY_COLS, EntityRow, buildEntitiesDisplayRows, cellKey, cellLabel, isLimitedForRow } from './utils'
import { CheckboxWithPolicyBadge, PolicyMatchesReadOnlyBlock, policyMatchesEntityCell } from './PolicyMatches'
import { PermissionPolicyDTO } from '../../domain/permission/PermissionPolicyDTO'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntitySelector = com.eidu.sharedlib.entity.selector.EntitySelector
import EntitySelectorPermission = com.eidu.sharedlib.entity.permission.EntitySelectorPermission
import Permission = com.eidu.sharedlib.entity.permission.Permission

type EntitiesSectionProps = {
  rows: readonly EntityRow[]
  open: boolean
  onToggleOpen: () => void
  cellPermissions: (row: EntityRow, col: ColDef) => PermissionWithId[]
  onToggleCell: (row: EntityRow, col: ColDef) => void
  onToggleColumn: (col: ColDef) => void
  hasColumnAll: (col: ColDef) => boolean
  expandedCell: string | null
  onToggleCellExpand: (key: string) => void
  onCloseExpansion: () => void
  entityTypes: readonly EntityType[]
  allowsPathForUser: boolean
  userEntityTypeId: EntityTypeId | undefined
  addPermission: (permission: Permission) => void
  replacePermission: (id: string, permission: Permission) => void
  removePermission: (id: string) => void
  selectedPolicies: ReadonlyArray<PermissionPolicyDTO>
}

const EntitiesSection = ({
  rows,
  open,
  onToggleOpen,
  cellPermissions,
  onToggleCell,
  onToggleColumn,
  hasColumnAll,
  expandedCell,
  onToggleCellExpand,
  onCloseExpansion,
  entityTypes,
  allowsPathForUser,
  userEntityTypeId,
  addPermission,
  replacePermission,
  removePermission,
  selectedPolicies,
}: EntitiesSectionProps) => {
  const ncols = 1 + ENTITY_COLS.length
  const hiddenCount = rows.length

  const columnState = (col: ColDef): 'checked' | 'indeterminate' | 'unchecked' => {
    if (hasColumnAll(col)) return 'checked'
    if (rows.length === 0) return 'unchecked'
    const filled = rows.filter((row) => cellPermissions(row, col).length > 0).length
    if (filled === 0) return 'unchecked'
    return 'indeterminate'
  }

  return (
    <SectionShell
      title="Types permissions"
      open={open}
      onToggleOpen={onToggleOpen}
      hasFooterToggle={hiddenCount > 0}
      footerLabel={`Show ${hiddenCount} more`}
      ncols={ncols}
      testId="matrix-section-entities"
    >
      <TableHead>
        <TableRow sx={{ bgcolor: 'action.hover' }}>
          <TableCell sx={{ minWidth: 240, borderRight: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2">Type</Typography>
          </TableCell>
          {ENTITY_COLS.map((col) => {
            const state = columnState(col)
            return (
              <TableCell key={col.action.toString()} sx={{ borderRight: 1, borderColor: 'divider', minWidth: 90 }}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Checkbox
                    size="small"
                    checked={state === 'checked'}
                    indeterminate={state === 'indeterminate'}
                    onChange={() => onToggleColumn(col)}
                    inputProps={{ 'aria-label': `Toggle all ${col.label}` }}
                    data-testid={`matrix-column-master-${col.label.toLowerCase()}`}
                    sx={{ p: 0.25 }}
                  />
                  <col.Icon fontSize="small" />
                  <Typography variant="subtitle2">{col.label}</Typography>
                  <Tooltip title="When checked, this permission applies to all current and future entity types.">
                    <InfoOutlined fontSize="small" sx={{ color: 'action.active', fontSize: '1rem' }} />
                  </Tooltip>
                </Stack>
              </TableCell>
            )
          })}
        </TableRow>
      </TableHead>
      <TableBody>
        {open &&
          buildEntitiesDisplayRows(rows).flatMap((display) => {
            if (display.kind === 'header') {
              return [
                <TableRow key={display.row.id}>
                  <TableCell
                    colSpan={ncols}
                    sx={{
                      bgcolor: 'action.selected',
                      fontWeight: 600,
                      borderBottom: 1,
                      borderColor: 'divider',
                      py: 0.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
                    >
                      {display.row.label}
                    </Typography>
                  </TableCell>
                </TableRow>,
              ]
            }
            const row = display.row
            const expandedCols = ENTITY_COLS.filter((col) => expandedCell === cellKey(row.id, col.action))

            const items = [
              <TableRow key={row.id} hover onClick={onCloseExpansion} data-testid={`matrix-row-${row.id}`}>
                <TableCell sx={{ borderRight: 1, borderColor: 'divider', pl: 3, bgcolor: 'action.hover' }}>
                  {row.label}
                </TableCell>
                {ENTITY_COLS.map((col) => {
                  const matching = cellPermissions(row, col)
                  const policyMatches = policyMatchesEntityCell(
                    selectedPolicies,
                    entityTypes,
                    row.entityTypeId,
                    col.action
                  )
                  const checked = matching.length > 0 || hasColumnAll(col)
                  const indeterminate =
                    matching.length > 0 &&
                    matching.some(({ permission }) => isLimitedForRow(permission, row.entityTypeId))
                  const key = cellKey(row.id, col.action)
                  const isExpanded = expandedCell === key
                  const label = cellLabel(matching)
                  return (
                    <TableCell key={col.action.toString()} sx={{ borderRight: 1, borderColor: 'divider' }}>
                      <Stack direction="row" alignItems="center" spacing={0}>
                        <CheckboxWithPolicyBadge matches={policyMatches}>
                          <Checkbox
                            size="small"
                            checked={checked}
                            indeterminate={indeterminate}
                            onChange={() => onToggleCell(row, col)}
                            inputProps={{ 'aria-label': `${row.label} ${col.label}` }}
                            data-testid={`matrix-checkbox-${row.id}-${col.label.toLowerCase()}`}
                            sx={{ p: 0.25 }}
                          />
                        </CheckboxWithPolicyBadge>
                        {(checked || isExpanded || policyMatches.length > 0) && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation()
                              onToggleCellExpand(key)
                            }}
                            data-testid={`matrix-expand-${row.id}-${col.label.toLowerCase()}`}
                            sx={{ p: 0.25 }}
                          >
                            {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                          </IconButton>
                        )}
                        {label && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 0.5, fontStyle: 'italic', fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                            data-testid={`matrix-label-${row.id}-${col.label.toLowerCase()}`}
                          >
                            {label}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                  )
                })}
              </TableRow>,
            ]

            if (expandedCols.length > 0) {
              items.push(
                <TableRow key={`${row.id}-detail`}>
                  <TableCell colSpan={ncols} sx={{ bgcolor: 'action.hover', p: 2 }}>
                    <Stack spacing={2}>
                      {expandedCols.map((col) => {
                        const policyMatches = policyMatchesEntityCell(
                          selectedPolicies,
                          entityTypes,
                          row.entityTypeId,
                          col.action
                        )
                        return (
                          <Stack key={col.action.toString()} spacing={1}>
                            <PolicyMatchesReadOnlyBlock matches={policyMatches} />
                            <CellEditor
                              row={row}
                              col={col}
                              permissions={cellPermissions(row, col)}
                              entityTypes={entityTypes}
                              allowsPath={col.allowsPath && allowsPathForUser}
                              userEntityTypeId={userEntityTypeId}
                              onAdd={() =>
                                addPermission(
                                  new EntitySelectorPermission(col.action, new EntitySelector.OfType(row.entityTypeId))
                                )
                              }
                              onReplace={replacePermission}
                              onDelete={removePermission}
                            />
                          </Stack>
                        )
                      })}
                    </Stack>
                  </TableCell>
                </TableRow>
              )
            }

            return items
          })}
      </TableBody>
    </SectionShell>
  )
}

export default EntitiesSection
